import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getCategoryAttributesWithValues,
  getCategoryAttribute,
  createCategoryAttribute,
  updateCategoryAttribute,
  deleteCategoryAttribute,
  type CategoryAttributeResponse,
  type CreateCategoryAttributeRequest,
} from "@/services/api";

const categoryAttributesWithValuesKeys = {
  all: ["categoryAttributesWithValues"] as const,
  lists: () => [...categoryAttributesWithValuesKeys.all, "list"] as const,
  list: (filters?: Record<string, unknown>) =>
    [...categoryAttributesWithValuesKeys.lists(), { filters }] as const,
  details: () => [...categoryAttributesWithValuesKeys.all, "detail"] as const,
  detail: (id: string | number) =>
    [...categoryAttributesWithValuesKeys.details(), id] as const,
};

export function useCategoryAttributesWithValues(
  options?: Omit<
    UseQueryOptions<CategoryAttributeResponse[], Error>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery<CategoryAttributeResponse[], Error>({
    queryKey: categoryAttributesWithValuesKeys.list(),
    queryFn: getCategoryAttributesWithValues,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useCategoryAttributeWithValues(
  id: string | number,
  options?: Omit<
    UseQueryOptions<CategoryAttributeResponse, Error>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery<CategoryAttributeResponse, Error>({
    queryKey: categoryAttributesWithValuesKeys.detail(id),
    queryFn: () => getCategoryAttribute(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useCreateCategoryAttributeWithValues() {
  const queryClient = useQueryClient();

  return useMutation<
    CategoryAttributeResponse,
    Error,
    CreateCategoryAttributeRequest
  >({
    mutationFn: createCategoryAttribute,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: categoryAttributesWithValuesKeys.lists(),
      });
      toast.success("Attribute created", {
        description: `"${data.name}" has been successfully created.`,
      });
    },
    onError: (error) => {
      toast.error("Failed to create attribute", {
        description:
          error.message || "An error occurred while creating the attribute.",
      });
    },
  });
}

export function useUpdateCategoryAttributeWithValues() {
  const queryClient = useQueryClient();

  return useMutation<
    CategoryAttributeResponse,
    Error,
    { id: string | number; data: CreateCategoryAttributeRequest }
  >({
    mutationFn: ({ id, data }) => updateCategoryAttribute(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: categoryAttributesWithValuesKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: categoryAttributesWithValuesKeys.detail(data.id),
      });
      toast.success("Attribute updated", {
        description: `"${data.name}" has been successfully updated.`,
      });
    },
    onError: (error) => {
      toast.error("Failed to update attribute", {
        description:
          error.message || "An error occurred while updating the attribute.",
      });
    },
  });
}

export function useDeleteCategoryAttributeWithValues() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string | number>({
    mutationFn: deleteCategoryAttribute,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: categoryAttributesWithValuesKeys.lists(),
      });
      toast.success("Attribute deleted", {
        description: "The attribute has been successfully deleted.",
      });
    },
    onError: (error) => {
      toast.error("Failed to delete attribute", {
        description:
          error.message || "An error occurred while deleting the attribute.",
      });
    },
  });
}

export { categoryAttributesWithValuesKeys };
